import React from 'react';

import { Dialog, Text, TextTypography as TYPOGRAPHY, Button, ButtonPriority as PRIORITY } from 'wix-ui-tpa';

import { usePanorama, useTranslation } from '@wix/yoshi-flow-editor';
import { connect } from '../../../common/components/runtime-context';
import {
  getCancelConfirmModalSubscription,
  isCancelConfirmModalOpen,
  isCancelConfirmModalSubmitting,
} from '../selectors';
import { classes } from './CancelConfirmModal.st.css';
import { RootState } from '../state';
import { Interactions } from '../../../types/interactions';
import { isRecurringSubscription } from '../domainUtils';
import { IActionCreators } from '../../../types/internal-types';

type RuntimeProps = ReturnType<typeof mapRuntimeToProps>;

export const CancelConfirmModal = ({ isOpen, subscription, submitting, actionCreators }: RuntimeProps) => {
  const panorama = usePanorama();
  const { t } = useTranslation();
  const isRecurring = isRecurringSubscription(subscription);
  return (
    <Dialog
      data-hook="cancel-confirm-modal"
      contentClassName={classes.dialogContent}
      wiredToSiteColors
      isOpen={isOpen}
      onClose={() => !submitting && actionCreators.closeCancelConfirmModal()}
      closeButtonAriaLabel={t('app.cancel-confirm-modal.close')}
    >
      {isOpen ? (
        <>
          <div className={classes.title}>
            <Text
              className={classes.titleText}
              data-hook="cancel-confirm-modal-title"
              typography={TYPOGRAPHY.largeTitle}
            >
              {t('app.cancel-confirm-modal.title', {
                subscriptionName: subscription.name,
                interpolation: { escapeValue: false },
              })}
            </Text>
          </div>
          <div data-hook="cancel-confirm-modal-description" className={classes.description}>
            <Text className={classes.text} typography={TYPOGRAPHY.runningText}>
              {t(
                isRecurring ? 'app.cancel-confirm-modal.description-recurring' : 'app.cancel-confirm-modal.description',
              )}
            </Text>
          </div>
          <div className={classes.actionContainer}>
            <Button
              className={classes.buttonPrimary}
              data-hook="CancelConfirmModal-cancel-btn"
              upgrade
              priority={PRIORITY.basicSecondary}
              disabled={submitting}
              onClick={() => actionCreators.closeCancelConfirmModal()}
            >
              {t('app.cancel-confirm-modal.cancel')}
            </Button>
            <Button
              className={classes.buttonSecondary}
              data-hook="CancelConfirmModal-confirm-btn"
              upgrade
              priority={PRIORITY.basic}
              disabled={submitting}
              onClick={() => {
                panorama.transaction(Interactions.SubscriptionCancel).start();
                actionCreators.confirmCancel();
              }}
            >
              {t('app.cancel-confirm-modal.confirm')}
            </Button>
          </div>
        </>
      ) : null}
    </Dialog>
  );
};

const mapRuntimeToProps = (state: RootState, _: {}, actionCreators: IActionCreators) => ({
  isOpen: isCancelConfirmModalOpen(state),
  subscription: getCancelConfirmModalSubscription(state),
  submitting: isCancelConfirmModalSubmitting(state),
  actionCreators,
});

export default connect(mapRuntimeToProps)(CancelConfirmModal);
